import { memo } from "react";
import DateHeader from "./DateHeader";
import KemChatBubble from "./KemChatBubble";
import UserChatBubble from "./UserChatBubble";
import { getDateKey } from "../utils/dateKey";
import type { KemCitationResource, TKemMessagePayload } from "../utils/types";

export interface KemChatMessageRowProps {
  rowIndex: number;
  message: TKemMessagePayload;
  previousMessage?: TKemMessagePayload;
  onKemBubbleImageLoad: (rowIndex: number) => void;
  setMessageAudioUrl?: (messageId: string, url: string) => void;
  handleMarkMessageVisible?: (messageId: string) => void;
}

const resourcesSame = (
  a?: KemCitationResource[],
  b?: KemCitationResource[],
): boolean => {
  if (a === b) return true;
  if (!a?.length && !b?.length) return true;
  if (!a || !b || a.length !== b.length) return false;

  return a.every(
    (item, index) =>
      item.value === b[index].value &&
      item.label === b[index].label &&
      item.url === b[index].url,
  );
};

const messagePayloadSame = (
  a: TKemMessagePayload | undefined,
  b: TKemMessagePayload | undefined,
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.id === b.id &&
    a.message === b.message &&
    a.created_at === b.created_at &&
    a.sender_enum === b.sender_enum &&
    a.output_audio_url === b.output_audio_url &&
    resourcesSame(a.resources, b.resources)
  );
};

const areChatMessageRowsEqual = (
  prev: KemChatMessageRowProps,
  next: KemChatMessageRowProps,
): boolean => {
  if (prev.rowIndex !== next.rowIndex) return false;
  if (prev.onKemBubbleImageLoad !== next.onKemBubbleImageLoad) return false;
  if (prev.setMessageAudioUrl !== next.setMessageAudioUrl) return false;
  if (prev.handleMarkMessageVisible !== next.handleMarkMessageVisible)
    return false;
  if (!messagePayloadSame(prev.message, next.message)) return false;

  // Date header visibility depends on the previous row’s calendar day.
  return (
    getDateKey(prev.previousMessage?.created_at) ===
    getDateKey(next.previousMessage?.created_at)
  );
};

const KemChatMessageRow = memo(
  ({
    rowIndex,
    message,
    previousMessage,
    onKemBubbleImageLoad,
    setMessageAudioUrl,
    handleMarkMessageVisible,
  }: KemChatMessageRowProps) => {
    const dk = getDateKey(message.created_at);
    const showHeader =
      rowIndex === 0 || dk !== getDateKey(previousMessage?.created_at);

    return (
      <div className="overflow-y-hidden">
        {showHeader && <DateHeader createdAt={message.created_at} />}
        {message.sender_enum === "user" ? (
          <UserChatBubble
            createdAt={message.created_at}
            message={message.message}
          />
        ) : message.message && message.id ? (
          <KemChatBubble
            createdAt={message.created_at}
            id={message.id}
            message={message.message}
            onImageLoad={() => {
              onKemBubbleImageLoad(rowIndex);
            }}
            onResolvedAudioUrl={(mid, url) => {
              setMessageAudioUrl?.(mid, url);
            }}
            onVisible={handleMarkMessageVisible}
            outputAudioUrl={message.output_audio_url}
            resources={message.resources}
          />
        ) : null}
      </div>
    );
  },
  areChatMessageRowsEqual,
);

KemChatMessageRow.displayName = "KemChatMessageRow";

export default KemChatMessageRow;
